import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * A resilient wrapper around React.lazy that automatically catches ChunkLoadError
 * or "Failed to fetch dynamically imported module" errors, and triggers a page
 * reload to fetch the latest compilation assets from the server.
 */
export function lazyRetry<T extends ComponentType>(
  componentImport: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      // Every route/component behind lazyRetry shares one deferred Tailwind
      // utility sheet. Fetch it in parallel with the requested module so the
      // homepage first paint never carries CSS for unopened surfaces.
      const [, component] = await Promise.all([
        import('../deferred.css'),
        componentImport()
      ]);
      return component;
    } catch (error: unknown) {
      console.error("[MOBILBO] Lazy import failed, initiating automatic chunk reload...", error);

      const runtimeError = error instanceof Error ? error : new Error(String(error));
      
      const errorStr = [
        runtimeError.message,
        runtimeError.name,
        runtimeError.stack,
        runtimeError.toString(),
        String(error)
      ].filter(Boolean).join(' ').toLowerCase();

      if (
        errorStr.includes('failed to fetch dynamically') ||
        errorStr.includes('chunkloaderror') ||
        errorStr.includes('dynamically imported module') ||
        errorStr.includes('loading chunk') ||
        errorStr.includes('error loading')
      ) {
        let shouldReload = false;
        const reloadKey = 'mobilbo_chunk_reload_attempted';
        const now = Date.now();

        try {
          const lastAttempt = sessionStorage.getItem(reloadKey);
          if (!lastAttempt || now - parseInt(lastAttempt, 10) > 10000) {
            sessionStorage.setItem(reloadKey, now.toString());
            shouldReload = true;
          }
        } catch (storageErr) {
          console.error("[MOBILBO] Storage error during reload handler, falling back to window.name:", storageErr);
          try {
            const nameParts = window.name ? window.name.split('__') : [];
            const lastReload = nameParts[0] === reloadKey ? parseInt(nameParts[1], 10) : 0;
            if (now - lastReload > 10000) {
              window.name = `${reloadKey}__${now}`;
              shouldReload = true;
            }
          } catch {
            shouldReload = true;
          }
        }

        if (shouldReload) {
          console.warn("[MOBILBO] Reloading page to fetch updated chunks...");
          window.location.reload();
          return new Promise(() => {}); // Return unresolved promise to block rendering until reload
        }
      }
      
      throw error;
    }
  });
}
